import React, { useEffect, useMemo, useState } from "react";
import { graphql } from "gatsby";
import Layout from "../components/layout";
import Seo from "../components/seo";
import PageContentBody from "../components/page-content-body";
import withPreview from "../utility/with-preview";
import { landingImpression, eventImpression } from "../utility/datalayer";
import { getCampaignBoxes } from "../utility/campaigns";
import findHrefLang from "../utility/find-hreflang";
import placeholderLogoImg from "../images/logo_haier_search.jpg";
import { normalizeRelativePath } from "../utility/paths";

const getGeneratedLocale = (liferayFields) => {
  const localeCategory = liferayFields?.taxonomyCategoryBriefs?.find(
    (category) => category?.parentTaxonomyVocabulary?.name === "locale-pages"
  );
  return localeCategory ? `/locale-${localeCategory.liferayId}/` : null;
};

const PageTemplate = ({
  data: {
    liferayPage,
    config,
    labelManagerLabels,
    newMainMenuFiltered,
    campaignsBannerTop,
    liferayMenuConfiguration,
  },
  location,
}) => {
  const [impression, setImpression] = useState(false);
  const isLanding = liferayPage?.isLanding && !!liferayPage?.isLanding?.length;
  useEffect(() => {
    if (!impression) {
      const eventCategory = liferayPage.liferayFields?.taxonomyCategoryBriefs?.find(
        (category) => category?.parentTaxonomyVocabulary?.name === "events"
      );
      if (isLanding) {
        landingImpression(liferayPage.slug);
        setImpression(true);
      } else if (eventCategory) {
        eventImpression(liferayPage.slug, eventCategory.name);
        setImpression(true);
      }
    }
  }, [liferayPage, impression, isLanding]);

  const campaignBoxesBannerTop = useMemo(
    () => getCampaignBoxes(campaignsBannerTop),
    [campaignsBannerTop]
  );
  const resultBannerTop =
    campaignBoxesBannerTop?.filter((item) => !!item.liferayFields.articleId) || [];

  const [isAnchor, setIsAnchor] = useState();

  const generatedLocale = useMemo(
    () => getGeneratedLocale(liferayPage.liferayFields),
    [liferayPage.liferayFields]
  );

  const isNewMenu =
    !!newMainMenuFiltered &&
    newMainMenuFiltered?.name &&
    liferayMenuConfiguration?.newMenuActive &&
    liferayMenuConfiguration?.copyrightText !== "";

  return (
    <Layout
      bannerTop={
        liferayPage?.customModule?.find((module) => module.__typename === "LiferayBannerTopMenu")
          ?.liferayFields?.articleId
          ? liferayPage?.customModule?.find(
              (module) => module.__typename === "LiferayBannerTopMenu"
            )
          : resultBannerTop[0]
      }
      hideMainBenefits={liferayPage.hideMainBenefits?.[0]}
      mainBenefits={
        liferayPage?.hideMainBenefits?.[0] !== "yes"
          ? liferayPage?.customModule?.filter(
              (module) => module.__typename === "LiferayMainBenefits"
            )
          : null
      }
      location={location}
      isEppMenu={liferayPage?.eppMenu === "yes"}
      isAnchor={isAnchor}
      hasSimpleMenu={liferayPage?.hasSimpleMenu?.[0] === "yes"}
      customFooter={liferayPage?.customModule?.find(
        (module) => module.__typename === "LiferayFooterMenu"
      )}
      generatedLocale={generatedLocale}
      newMenu={!!isNewMenu}
      newCustomFooter={liferayPage.newCustomFooter}
      newCustomSocialFooter={liferayPage.newCustomSocialFooter}
      newCustomPaymentFooter={liferayPage.newCustomPaymentFooter}
      isDark={liferayPage?.isDarkTheme === true}
      customLogo={liferayPage?.customLogo}
    >
      <span className="tag-argument d-none">{labelManagerLabels.informationPage}</span>
      {liferayPage.productCategories.map((category) => (
        <span className="tag-category d-none" key={category}>
          {category}
        </span>
      ))}
      <PageContentBody
        page={liferayPage}
        location={location}
        origin={isLanding ? "Landing page" : null}
        config={config}
        labelManagerLabels={labelManagerLabels}
        setIsAnchor={setIsAnchor}
        isNewMenu={isNewMenu}
      />
    </Layout>
  );
};

export const Head = ({ data: { liferayPage }, location }) => {
  const ogImage = useMemo(() => {
    const sliderHero = liferayPage.contentBody?.[0];

    if (sliderHero?.moduleTitleFieldSet) {
      const module = sliderHero.moduleTitleFieldSet.find((module) =>
        module?.titleT1FieldSet?.some((item) => item.bgImageMobileT1 || item.bgImageT1)
      );
      const moduleSlider = sliderHero.moduleTitleFieldSet.find((module) =>
        module?.titleT1FieldSet?.some((item) => item.slideForegroundImage)
      );
      return module
        ? module.titleT1FieldSet.find((item) => item.bgImageMobileT1)
          ? module.titleT1FieldSet.find((item) => item.bgImageMobileT1).bgImageMobileT1.node.resize
          : moduleSlider
          ? moduleSlider.titleT1FieldSet.find((item) => item.slideForegroundImage)
              .slideForegroundImage.node.resize
          : module.titleT1FieldSet.find((item) => item.bgImageT1).bgImageT1.node.resize
        : placeholderLogoImg;
    }
    return placeholderLogoImg;
  }, [liferayPage.contentBody]);
  const hrefLanguages = useMemo(() => findHrefLang(liferayPage), [liferayPage]);

  const canonicalPath = useMemo(() => {
    if (typeof window !== "undefined" && location) {
      const blogPage = liferayPage.contentBody.find(
        (item) => item?.__typename === "LiferayBlogPage" && item?.viewBlog?.[0] === "List Page"
      );
      if (blogPage) {
        const searchParams = new URLSearchParams(window.location.search);
        const canonicalParams = new URLSearchParams();
        if (!searchParams.has("page") && !searchParams.has("category")) {
          return liferayPage.slug;
        }
        if (searchParams.has("category")) {
          canonicalParams.set("category", searchParams.get("category"));
        }
        if (searchParams.has("page")) {
          canonicalParams.set("page", searchParams.get("page"));
        }
        return `${normalizeRelativePath(liferayPage.slug)}?${canonicalParams}`;
      }
    }
    return liferayPage.slug;
  }, [liferayPage, location]);

  const generatedLocale = useMemo(
    () => getGeneratedLocale(liferayPage.liferayFields),
    [liferayPage.liferayFields]
  );

  return (
    <Seo
      title={liferayPage.liferayFields.title}
      path={canonicalPath}
      ogImage={ogImage}
      seo={liferayPage?.seo}
      hrefLanguages={hrefLanguages}
      excludeFromSitemap={liferayPage?.excludeFromSitemap}
      noIndexNoFollow={liferayPage?.noIndexNoFollow}
      generatedLocale={generatedLocale}
    />
  );
};

export default withPreview(PageTemplate, { subField: "liferayPage", fixed: true });
export const query = graphql`
  query ($id: String) {
    liferayPage(id: { eq: $id }) {
      excludeFromSitemap
      noIndexNoFollow
      slug
      eppMenu
      liferayFields {
        siteId
        articleId
        title
        taxonomyCategoryBriefs {
          liferayId
          name
          parentTaxonomyVocabulary {
            name
          }
        }
      }
      parentPages {
        page {
          ... on LiferayPage {
            liferayFields {
              title
            }
            slug
          }
          ... on LiferayPageLine {
            liferayFields {
              title
            }
            slug
          }
          ... on LiferayPageCrossLine {
            liferayFields {
              title
            }
            slug
          }
          ... on LiferayPageExternalLink {
            liferayFields {
              title
            }
            externalLink
          }
        }
        menuItem {
          name
          useCustomName
        }
      }
      hasSimpleMenu
      customLogo {
        value {
          description
        }
        node {
          publicURL
          gatsbyImageData(layout: FULL_WIDTH)
        }
      }
      customModule {
        __typename
        ... on LiferayBannerTopMenu {
          ...BannerTopMenuFragment
        }
        ... on LiferayMainBenefits {
          id
          fieldsGroup {
            mainBenefitsTitle
            mainBenefitsIcon {
              value {
                description
              }
              node {
                publicURL
              }
            }
            mainBenefitsPosition
            mainBenefitsLink
          }
        }
        ... on LiferayFooterMenu {
          columnNameFieldSet {
            columnName
            page {
              ... on LiferayPage {
                liferayFields {
                  title
                }
                slug
              }
              ... on LiferayPageLine {
                liferayFields {
                  title
                }
                slug
              }
              ... on LiferayPageCrossLine {
                liferayFields {
                  title
                }
                slug
              }
              ... on LiferayPageExternalLink {
                liferayFields {
                  title
                }
                externalLink
                target
              }
            }
          }
          payAndSocialFooter {
            columnPayAndSocial
            pagePayAndSocial {
              ... on LiferayPageExternalLink {
                liferayFields {
                  title
                }
                icon
                externalLink
                target
                alternativeIcon {
                  alternativeIcon {
                    value {
                      description
                    }
                    node {
                      publicURL
                    }
                  }
                  height
                  width
                }
              }
            }
          }
          legalInfo
          localeContent {
            ... on LiferayPageExternalLink {
              liferayFields {
                title
              }
              icon
              image {
                node {
                  resize(width: 20, quality: 100) {
                    src
                  }
                }
                value {
                  description
                }
              }
              externalLink
              target
            }
          }
        }
      }
      isLanding
      isDarkTheme
      hideMainBenefits
      seo {
        ...SeoFragment
      }
      chatSticky {
        ... on LiferayChatSticky {
          liferayFields {
            title
          }
          icon {
            node {
              publicURL
            }
          }
          link
        }
      }
      productCategories
      ...ContentBodyFragment
      newCustomFooterName
      newCustomFooterSocialName
      newCustomFooterPaymentName
      newCustomSocialFooter {
        name
        navigationMenuItems {
          useCustomName
          name
          content {
            ... on LiferayPage {
              id
              slug
              liferayFields {
                title
              }
            }
            ... on LiferayPageExternalLink {
              id
              liferayFields {
                title
              }
              target
              externalLink
              icon
              alternativeIcon {
                alternativeIcon {
                  value {
                    description
                  }
                  node {
                    publicURL
                  }
                }
                height
                width
              }
            }
            ... on LiferayPageLine {
              id
              slug
              liferayFields {
                title
              }
            }
            ... on LiferayPageCrossLine {
              id
              slug
              liferayFields {
                title
              }
            }
          }
          navigationMenuItems {
            useCustomName
            name
            content {
              ... on LiferayPage {
                id
                liferayFields {
                  title
                }
                slug
              }
              ... on LiferayPageCrossLine {
                id
                liferayFields {
                  title
                }
                slug
              }
              ... on LiferayPageExternalLink {
                id
                liferayFields {
                  title
                }
                externalLink
                target
                icon
                alternativeIcon {
                  alternativeIcon {
                    value {
                      description
                    }
                    node {
                      publicURL
                    }
                  }
                  height
                  width
                }
              }
              ... on LiferayPageLine {
                id
                liferayFields {
                  title
                }
                pageNameMenu
                slug
                lineImageFieldSet {
                  lineImage {
                    node {
                      gatsbyImageData(width: 500, layout: CONSTRAINED)
                    }
                  }
                  altImage
                }
                allBabelProdotto {
                  id
                }
              }
            }
          }
        }
      }
      newCustomPaymentFooter {
        name
        navigationMenuItems {
          useCustomName
          name
          content {
            ... on LiferayPage {
              id
              slug
              liferayFields {
                title
              }
            }
            ... on LiferayPageExternalLink {
              id
              liferayFields {
                title
              }
              target
              externalLink
              icon
              alternativeIcon {
                alternativeIcon {
                  value {
                    description
                  }
                  node {
                    publicURL
                  }
                }
                height
                width
              }
            }
            ... on LiferayPageLine {
              id
              slug
              liferayFields {
                title
              }
            }
            ... on LiferayPageCrossLine {
              id
              slug
              liferayFields {
                title
              }
            }
          }
          navigationMenuItems {
            useCustomName
            name
            content {
              ... on LiferayPage {
                id
                liferayFields {
                  title
                }
                slug
              }
              ... on LiferayPageCrossLine {
                id
                liferayFields {
                  title
                }
                slug
              }
              ... on LiferayPageExternalLink {
                id
                liferayFields {
                  title
                }
                externalLink
                target
                icon
                alternativeIcon {
                  alternativeIcon {
                    value {
                      description
                    }
                    node {
                      publicURL
                    }
                  }
                  height
                  width
                }
              }
              ... on LiferayPageLine {
                id
                liferayFields {
                  title
                }
                pageNameMenu
                slug
                lineImageFieldSet {
                  lineImage {
                    node {
                      gatsbyImageData(width: 500, layout: CONSTRAINED)
                    }
                  }
                  altImage
                }
                allBabelProdotto {
                  id
                }
              }
            }
          }
        }
      }
      newCustomFooter {
        name
        navigationMenuItems {
          useCustomName
          name
          content {
            ... on LiferayPage {
              id
              slug
              liferayFields {
                title
              }
            }
            ... on LiferayPageExternalLink {
              id
              liferayFields {
                title
              }
              target
              externalLink
            }
            ... on LiferayPageLine {
              id
              slug
              liferayFields {
                title
              }
            }
            ... on LiferayPageCrossLine {
              id
              slug
              liferayFields {
                title
              }
            }
          }
          navigationMenuItems {
            useCustomName
            name
            content {
              ... on LiferayPage {
                id
                liferayFields {
                  title
                }
                slug
              }
              ... on LiferayPageCrossLine {
                id
                liferayFields {
                  title
                }
                slug
              }
              ... on LiferayPageExternalLink {
                id
                liferayFields {
                  title
                }
                externalLink
                target
              }
              ... on LiferayPageLine {
                id
                liferayFields {
                  title
                }
                pageNameMenu
                slug
                lineImageFieldSet {
                  lineImage {
                    node {
                      gatsbyImageData(width: 500, layout: CONSTRAINED)
                    }
                  }
                  altImage
                }
                allBabelProdotto {
                  id
                }
              }
            }
          }
        }
      }
    }
    campaignsBannerTop: globalCampaigns(positions: ["BANNER TOP | Banner Promo"]) {
      date {
        pmStart
        pmEnd
      }
      filterField {
        pmInStock
        pmSpecialPrice
      }
      boxAndPosition {
        pmBoxPosition
        webContent {
          ...BannerTopMenuFragment
        }
      }
    }
    config: liferayGeneralConfiguration {
      isReevooActiveFieldSet {
        isReevooActive
        isBazaarVoiceActive
      }
      virtualRealityActiveFieldSet {
        virtualRealityActive
      }
      wtbAndRetailersActive
      priceInPerc: priceInPerc
      tagActive
      tagColor
      haierStEcommLines {
        productLine
      }
      invertProductFamily
    }
    labelManagerLabels: labelManagerLabels {
      home
      addToCart
      preorder
      whereToBuy
      showProduct
      compare
      removeFromComparison
      buyNow
      product
      products
      openDetails
      closeDetails
      informationPage
      buyOnHaierShop
    }
    newMainMenuFiltered {
      name
    }
    liferayMenuConfiguration {
      newMenuActive
      copyrightText
    }
  }
`;
